'use client';

import { useState, useEffect } from 'react';
import { BlogPost } from '@prisma/client';
import { BlogCard } from '@/components/blog/BlogCard';
import { SearchBar } from '@/components/blog/SearchBar';
import { AgeRangePicker } from '@/components/blog/AgeRangePicker';
import { Gamepad2, ArrowLeft, Sparkles, Star, Zap, Trophy, Smile } from 'lucide-react';
import Link from 'next/link';
import { ageRangesOverlap, getAgeFilterOptions } from '@/lib/age-range-utils';

export default function PlayPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAgeRange, setSelectedAgeRange] = useState<string | undefined>();
  const [availableAgeRanges, setAvailableAgeRanges] = useState<string[]>([]);

  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    filterPosts();
  }, [posts, searchQuery, selectedAgeRange]);

  const fetchPosts = async () => {
    try {
      const response = await fetch('/api/blogs?category=PLAY');
      const data = await response.json();
      const fetchedPosts = data.posts || [];
      setPosts(fetchedPosts);
      
      // Extract and calculate age filter options
      const ageRanges = fetchedPosts
        .map((post: BlogPost) => post.ageRange)
        .filter((range: any): range is string => !!range);
      setAvailableAgeRanges(getAgeFilterOptions(ageRanges));
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPosts = () => {
    let filtered = [...posts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.summary?.toLowerCase().includes(query) ||
          post.content.toLowerCase().includes(query)
      );
    }

    if (selectedAgeRange) {
      filtered = filtered.filter((post) => {
        if (!post.ageRange) return false;
        return ageRangesOverlap(selectedAgeRange, post.ageRange);
      });
    }

    setFilteredPosts(filtered);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #F0F9FF 0%, #E0F2FE 50%, #F0F9FF 100%)', position: 'relative', overflow: 'hidden' }}>
      {/* Animated background orbs */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '5%',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(168, 216, 230, 0.25) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 15s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        top: '40%',
        right: '8%',
        width: '350px',
        height: '350px',
        background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        animation: 'float 18s ease-in-out infinite 2s',
        pointerEvents: 'none',
      }} />

      {/* Hero Section */}
      <section style={{ padding: '4rem 1.5rem 4.5rem', position: 'relative', overflow: 'hidden' }}>
        {/* Floating sparkles */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 6 + 3}px`,
              height: `${Math.random() * 6 + 3}px`,
              background: ['#A8D8E6', '#8BC9DD', '#C5E8FD'][i % 3],
              borderRadius: '50%',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${10 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 3}s`,
              opacity: 0.4,
              boxShadow: '0 0 8px currentColor',
              pointerEvents: 'none',
            }}
          />
        ))}
        
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          {/* Back Button */}
          <Link href="/blogs" style={{ textDecoration: 'none' }}>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.625rem',
                color: '#0284C7',
                fontSize: '1rem',
                fontWeight: 700,
                marginBottom: '2.5rem',
                padding: '0.75rem 1.5rem',
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                borderRadius: '50px',
                border: '2px solid rgba(168, 216, 230, 0.3)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(168, 216, 230, 0.2)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateX(-4px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(168, 216, 230, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateX(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(168, 216, 230, 0.2)';
              }}
            >
              <ArrowLeft size={20} strokeWidth={2.5} />
              Back to Blogs
            </div>
          </Link>

          <div style={{ textAlign: 'center' }}>
            {/* Icon Container */}
            <div style={{ position: 'relative', marginBottom: '2.5rem', display: 'inline-block' }}>
              <div style={{
                position: 'absolute',
                width: '220px',
                height: '220px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'radial-gradient(circle, rgba(168, 216, 230, 0.4) 0%, transparent 70%)',
                filter: 'blur(40px)',
                animation: 'pulse 3s ease-in-out infinite',
                pointerEvents: 'none',
              }} />
              
              <div style={{
                width: '140px',
                height: '140px',
                borderRadius: '36px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                boxShadow: '0 20px 60px -15px rgba(168, 216, 230, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
                position: 'relative',
                border: '3px solid rgba(168, 216, 230, 0.3)',
                animation: 'float 4s ease-in-out infinite',
              }}>
                <div style={{
                  position: 'absolute',
                  inset: '10px',
                  borderRadius: '30px',
                  background: 'linear-gradient(135deg, #A8D8E6, #8BC9DD)',
                  opacity: 0.15,
                }} />
                
                <Gamepad2 
                  size={64} 
                  strokeWidth={2.5}
                  color="#0284C7"
                  style={{
                    position: 'relative',
                    zIndex: 1,
                    filter: 'drop-shadow(0 4px 12px rgba(2, 132, 199, 0.4))',
                  }}
                />
              </div>
            </div>

            {/* Category badge */}
            <div style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '8px', 
              background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.15), rgba(197, 232, 253, 0.15))',
              padding: '8px 20px',
              borderRadius: '50px',
              border: '2px solid rgba(168, 216, 230, 0.3)',
              marginBottom: '20px',
            }}>
              <Trophy size={16} color="#0284C7" />
              <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0284C7', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Games & Activities
              </span>
            </div>

            <h1
              style={{
                fontSize: 'clamp(3rem, 6vw, 5rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #0284C7 0%, #0EA5E9 50%, #A8D8E6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '1.5rem',
                letterSpacing: '-0.03em',
                lineHeight: 1,
              }}
            >
              Play
            </h1>
            
            <p
              style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.5rem)',
                color: '#64748b',
                fontWeight: 500,
                maxWidth: '750px',
                margin: '0 auto 2.5rem',
                lineHeight: 1.7,
              }}
            >
              Discover <strong style={{ color: '#0284C7' }}>fun games</strong> and <strong style={{ color: '#0EA5E9' }}>creative activities</strong> for endless entertainment!
            </p>

            {/* Feature badges */}
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '1rem',
              justifyContent: 'center',
              alignItems: 'center',
            }}>
              {[
                { icon: <Smile size={16} />, text: 'Super Fun', color: '#F59E0B' },
                { icon: <Zap size={16} />, text: 'Engaging', color: '#10B981' },
                { icon: <Star size={16} />, text: 'Creative', color: '#EF4444' },
              ].map((badge, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.5rem',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: `2px solid ${badge.color}20`,
                    fontSize: '0.9375rem',
                    fontWeight: 700,
                    color: badge.color,
                    boxShadow: `0 4px 16px ${badge.color}15`,
                  }}
                >
                  {badge.icon}
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section style={{ 
        padding: '2.5rem 1.5rem', 
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(20px)',
        borderTop: '2px solid rgba(168, 216, 230, 0.2)',
        borderBottom: '2px solid rgba(168, 216, 230, 0.2)',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.03)',
        position: 'relative',
        zIndex: 2,
      }}>
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <SearchBar
              onSearch={setSearchQuery}
              placeholder="Search for fun activities..."
            />
          </div>
          <AgeRangePicker
            selectedAgeRange={selectedAgeRange}
            onSelect={setSelectedAgeRange}
            availableRanges={availableAgeRanges}
          />
        </div>
      </section>

      {/* Posts Grid */}
      <section style={{ padding: '4rem 1.5rem', position: 'relative', zIndex: 1 }}>
        <div className="container" style={{ maxWidth: '1280px', margin: '0 auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '5rem 2rem' }}>
              <div style={{
                width: '80px',
                height: '80px',
                border: '5px solid rgba(168, 216, 230, 0.2)',
                borderTopColor: '#0284C7',
                borderRadius: '50%',
                margin: '0 auto 1.5rem',
                animation: 'spin 1s linear infinite',
              }} />
              <p style={{ color: '#64748b', fontSize: '1.125rem', fontWeight: 600 }}>
                Loading fun activities...
              </p>
            </div>
          ) : filteredPosts.length === 0 ? (
            <div style={{
              background: 'rgba(255, 255, 255, 0.7)',
              backdropFilter: 'blur(30px)',
              borderRadius: '48px',
              padding: 'clamp(3rem, 8vw, 5rem) clamp(2rem, 5vw, 4rem)',
              textAlign: 'center',
              border: '3px solid rgba(168, 216, 230, 0.3)',
              boxShadow: '0 30px 80px -20px rgba(168, 216, 230, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.3) inset',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Floating sparkles */}
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    position: 'absolute',
                    width: `${Math.random() * 6 + 3}px`,
                    height: `${Math.random() * 6 + 3}px`,
                    background: '#A8D8E6',
                    borderRadius: '50%',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    animation: `float ${10 + Math.random() * 5}s ease-in-out infinite ${Math.random() * 3}s`,
                    opacity: 0.4,
                  }}
                />
              ))}

              <Gamepad2 
                size={80} 
                strokeWidth={2}
                color="#0284C7"
                style={{
                  marginBottom: '2rem',
                  filter: 'drop-shadow(0 8px 16px rgba(2, 132, 199, 0.3))',
                  animation: 'float 4s ease-in-out infinite',
                }}
              />
              
              <h3 style={{
                fontSize: 'clamp(2rem, 5vw, 3rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #0284C7 0%, #0EA5E9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '1rem',
                letterSpacing: '-0.02em',
              }}>
                No Activities Found
              </h3>
              
              <p style={{
                fontSize: 'clamp(1.125rem, 2.5vw, 1.375rem)',
                color: '#64748b',
                maxWidth: '600px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}>
                Try adjusting your search or filters to find fun games!
              </p>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
              gap: '2.5rem',
            }}>
              {filteredPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>

      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.02); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
